import { useEffect, useRef } from 'react';
import type { CalloutMode, RoomState } from '@tv-casino/shared';
import {
  calloutSnapshot,
  clockCallout,
  dealerCallouts,
  gameClips,
  PARTY_CLIPS,
  partyClockCallouts,
  type Callout,
  type CalloutSnapshot,
} from '@tv-casino/viewmodels/lib/callouts.js';
import { hushVoice, loadVoice, say, setVoiceVolume } from './voice.js';

/** How many spoken keys to remember; far more than one hand ever makes. */
const KEYS_KEPT = 64;

/** Who decides how loud — and how chatty — the dealer is here. */
export interface CalloutOptions {
  /**
   * `'room'` is the TV: the host's voice slider, published in room state.
   * `'device'` is a phone, where the player's own audio control owns the
   * level — this hook leaves that fader exactly where that control put it,
   * so the host turning the TV's dealer down cannot reach someone's
   * headphones.
   */
  volume?: 'room' | 'device';
  /**
   * A device's own call-out level, from its audio panel. Given, it replaces
   * the room's mode outright: a phone on "every call" hears every call even
   * while the TV sits on "decisions", and muting the TV dealer mutes nobody's
   * headphones.
   */
  mode?: CalloutMode;
}

/**
 * The dealer, driven off published room state: `dealerCallouts` decides what
 * each broadcast earns, and this hands it to `voice.ts` on time. A call
 * holding for its effect — the ball drop, the dice landing — survives the
 * next broadcast rather than being cancelled by it.
 *
 * The TV runs it, and so does every phone that has unmuted itself: both read
 * the same broadcasts and reach the same calls. What each can tune is its
 * own: the TV takes the host's room settings, a phone takes the level and
 * mode its own audio panel chose.
 */
export function useDealerCallouts(
  state: RoomState | null,
  { volume = 'room', mode: ownMode }: CalloutOptions = {},
): void {
  const mode: CalloutMode = ownMode ?? state?.calloutMode ?? 'off';
  const game = state?.gameType ?? null;
  const voiceVolume = state?.voiceVolume ?? 100;
  const was = useRef<CalloutSnapshot | null>(null);
  const said = useRef<string[]>([]);
  const timers = useRef(new Set<number>());

  useEffect(() => {
    if (volume === 'device') return;
    setVoiceVolume(voiceVolume);
  }, [voiceVolume, volume]);
  const partyOn = Boolean(state?.party);
  useEffect(
    () => loadVoice(mode === 'off' || !game ? [] : [...gameClips(game), ...(partyOn ? PARTY_CLIPS : [])]),
    [mode, game, partyOn],
  );

  const speak = (callout: Callout, delayMs: number) => {
    const fire = () => {
      if (said.current.includes(callout.key)) return;
      said.current = [...said.current, callout.key].slice(-KEYS_KEPT);
      say(callout);
    };
    if (delayMs <= 0) return fire();
    const timer = window.setTimeout(() => {
      timers.current.delete(timer);
      fire();
    }, delayMs);
    timers.current.add(timer);
  };

  useEffect(() => {
    const next = calloutSnapshot(state);
    const calls = dealerCallouts(was.current, next, mode);
    was.current = next;
    for (const callout of calls) speak(callout, callout.delayMs ?? 0);
  }, [state, mode]);

  // "Last call" and the shooter nudge ride the clock, not a broadcast.
  const snapshot = calloutSnapshot(state);
  const clockKey = snapshot?.clock ? `${snapshot.clock.kind}:${snapshot.clock.endsAt}:${snapshot.phase}` : null;
  useEffect(() => {
    const due = clockCallout(snapshot, mode, Date.now());
    if (!due) return;
    const timer = window.setTimeout(() => speak(due.callout, 0), due.at - Date.now());
    return () => window.clearTimeout(timer);
    // The clock key carries everything `snapshot` contributes here.
  }, [clockKey, mode]);

  // Party mode's time warnings ride its schedule the same way.
  const party = snapshot?.party;
  const partyKey = party ? `${party.slotEndsAt}:${party.endsAt}:${party.due}:${party.done}` : null;
  useEffect(() => {
    const timers = partyClockCallouts(snapshot, mode, Date.now()).map((due) =>
      window.setTimeout(() => speak(due.callout, 0), due.at - Date.now()),
    );
    return () => {
      for (const timer of timers) window.clearTimeout(timer);
    };
    // The party key carries everything `snapshot` contributes here.
  }, [partyKey, mode]);

  useEffect(() => {
    if (mode === 'off') hushVoice();
  }, [mode]);

  useEffect(
    () => () => {
      for (const t of timers.current) window.clearTimeout(t);
      hushVoice();
    },
    [],
  );
}
